import React, { useEffect, useRef, useState } from 'react';
import {
  enableNotifications,
  isPushEnabledFor,
  isSubscribedTo,
  pushCapability,
} from '../utils/pushNotifications';

/**
 * Asks a guest once, in our own words, whether they want this venue's notifications. Only a tap on
 * "Allow" opens the browser's own permission prompt: iPhone requires a tap there, and Chrome on
 * Android hides prompts that appear without one. "Not now" is remembered for a week.
 *
 * Shown only when the venue has notifications switched on, the device can receive them (on iPhone
 * that means opened from the Home Screen) and this device is not subscribed yet.
 */
export const PROMPT_DELAY_MS = 3000;
export const PROMPT_SNOOZE_MS = 7 * 24 * 60 * 60 * 1000;
// After a failure that is probably temporary (our server, the network) ask again sooner.
export const PROMPT_RETRY_MS = 24 * 60 * 60 * 1000;
// A guest who turned notifications off on purpose is left alone for longer.
export const PROMPT_OPT_OUT_MS = 30 * 24 * 60 * 60 * 1000;
export const PUSH_CHANGED_EVENT = 'push-subscription-changed';

// Venues this page session already settled (not switched on, cannot receive, or already subscribed), so
// leaving and re-entering a page does not repeat the checks. Failures and unknowns are never remembered.
const settledVenues = new Set();
export const resetPromptSession = () => settledVenues.clear();

const snoozeKey = venueId => `push_prompt_snoozed_${venueId}`;

function isSnoozed(venueId, now = Date.now()) {
  try {
    return now < Number(localStorage.getItem(snoozeKey(venueId)) || 0);
  } catch {
    return false;
  }
}

function snooze(venueId, duration = PROMPT_SNOOZE_MS, now = Date.now()) {
  try { localStorage.setItem(snoozeKey(venueId), String(now + duration)); } catch { /* Ask again next visit. */ }
}

/** The guest switched this venue off in the drawer: do not offer it again for a while. */
export function rememberOptOut(venueId) {
  snooze(venueId, PROMPT_OPT_OUT_MS);
}

/** The CMS shows the compendium in a preview frame; an editor must not be asked there. */
export function isFramed(win = window) {
  try {
    return win.self !== win.top;
  } catch {
    return true;
  }
}

// hold: another dialog (the Home Screen help) is on screen. This one waits, so focus is not taken from it, and
// appears when that one is closed.
const VenueNotificationPrompt = ({ venueId, venueName, hold = false }) => {
  const [wanted, setOpen] = useState(false);
  const open = wanted && !hold;
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const busyRef = useRef(false);
  const boxRef = useRef(null);
  const primaryRef = useRef(null);

  // Keyboard and screen-reader users: move focus into the dialog, give it back when it closes.
  useEffect(() => {
    if (!open) return undefined;
    const before = document.activeElement;
    return () => { if (before && typeof before.focus === 'function') before.focus(); };
  }, [open]);

  // The page behind the dialog must not scroll while it is open.
  useEffect(() => {
    if (!open) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    // While the request runs the buttons are disabled; keep focus on the dialog itself.
    const target = busy ? boxRef.current : primaryRef.current;
    if (target) target.focus();
  }, [open, busy, failed]);

  useEffect(() => {
    if (!venueId || isFramed()) return undefined;
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        if (settledVenues.has(venueId) || isSnoozed(venueId)) return;
        const enabled = await isPushEnabledFor(venueId);
        if (enabled === false) settledVenues.add(venueId);
        if (enabled !== true) return;
        if (cancelled) return;
        if (pushCapability().state !== 'ready') {
          settledVenues.add(venueId);
          return;
        }
        // Only an explicit "not subscribed": an unknown status must not nag the guest.
        const subscribed = await isSubscribedTo(venueId);
        if (subscribed === true) settledVenues.add(venueId);
        if (subscribed !== false) return;
        if (!cancelled) setOpen(true);
      } catch {
        // Never let a failed check interrupt the page.
      }
    }, PROMPT_DELAY_MS);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [venueId]);

  if (!open) return null;

  const close = () => setOpen(false);

  const notNow = () => {
    snooze(venueId);
    close();
  };

  // Called straight from the tap so the browser accepts its permission prompt.
  const allow = async () => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    let result;
    try {
      result = await enableNotifications(venueId);
    } catch {
      result = { ok: false, reason: 'unavailable' };
    }
    busyRef.current = false;
    setBusy(false);
    if (result.ok) {
      settledVenues.add(venueId);
      window.dispatchEvent(new Event(PUSH_CHANGED_EVENT));
      close();
    } else if (result.reason === 'unavailable') {
      // Probably temporary: say so instead of vanishing, and ask again tomorrow.
      snooze(venueId, PROMPT_RETRY_MS);
      setFailed(true);
    } else {
      // The guest said no (or the browser did): respect it for a week.
      snooze(venueId);
      close();
    }
  };

  const name = venueName || 'this venue';

  // Escape means "not now"; Tab stays inside the dialog.
  const onKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      if (!busy) (failed ? close : notNow)();
      return;
    }
    if (event.key !== 'Tab' || !boxRef.current) return;
    const buttons = Array.from(boxRef.current.querySelectorAll('button:not([disabled])'));
    if (!buttons.length) {
      event.preventDefault();
      return;
    }
    const first = buttons[0];
    const last = buttons[buttons.length - 1];
    const current = document.activeElement;
    if (event.shiftKey && (current === first || !buttons.includes(current))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (current === last || !buttons.includes(current))) {
      event.preventDefault();
      first.focus();
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="notification-prompt-title"
      aria-describedby="notification-prompt-text"
      data-testid="notification-prompt"
      onKeyDown={onKeyDown}
      className="fixed inset-0 z-[60] p-4 flex items-center justify-center bg-black bg-opacity-40 overscroll-contain touch-none"
    >
      <div
        ref={boxRef}
        tabIndex={-1}
        className="w-full max-w-md rounded-2xl bg-white p-4 shadow-2xl border border-gray-200 outline-none"
      >
        <p id="notification-prompt-title" className="font-semibold text-gray-900">Get updates from {name}?</p>
        <p id="notification-prompt-text" className="mt-1 text-sm text-gray-600" data-testid="notification-prompt-text">
          {failed
            ? 'Notifications are not available right now. Please try again later.'
            : `Turn on notifications to receive news and offers from ${name} on this phone. You can turn them off any time.`}
        </p>
        <div className="mt-4 flex gap-3 justify-end">
          <button
            type="button"
            ref={failed ? primaryRef : null}
            onClick={failed ? close : notNow}
            disabled={busy}
            className="px-4 py-2 rounded-lg text-gray-700"
          >
            {failed ? 'Close' : 'Not now'}
          </button>
          {!failed && (
            <button
              type="button"
              ref={primaryRef}
              onClick={allow}
              disabled={busy}
              className="px-4 py-2 rounded-lg bg-gray-900 text-white font-semibold"
            >
              {busy ? 'One moment…' : 'Allow'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const NotificationPrompt = props => <VenueNotificationPrompt key={props.venueId} {...props} />;

export default NotificationPrompt;
